import type { RouterOutput } from "@lattice/backend/trpc";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@lattice/frontend/components/ui/table";
import { Tooltip } from "@lattice/frontend/components/ui/tooltip";
import { format } from "date-fns";
import { CircleQuestionMarkIcon, SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";

import { routes } from "@lattice/frontend/lib/routes/routes";

export default function StockInsiderTradesTable({
  data,
}: {
  data: RouterOutput["stock"]["get_insider_trades"];
}) {
  const insider_grouped = Object.groupBy(data, ({ transactionDate }) =>
    transactionDate.toISOString(),
  );

  return (
    <div className="rounded-[2px] border border-primary/30 relative max-w-full max-h-full">
      <Table
        className="text-xs sm:text-sm md:text-base"
        containerClass="overflow-y-auto max-h-[600px]"
      >
        <TableHeader className="bg-primary/20 backdrop-blur-sm">
          <TableRow className="border-b-2 border-primary/30 hover:bg-transparent">
            <TableHead className="text-xs font-semibold text-foreground uppercase tracking-wider pl-5">
              TRANSACTION DATE
            </TableHead>
            <TableHead className="text-xs font-semibold text-foreground uppercase tracking-wider pl-1">
              BUY/SELL
            </TableHead>
            <TableHead className="text-xs font-semibold text-foreground uppercase tracking-wider pl-1">
              INSIDER NAME
            </TableHead>
            <TableHead className="text-xs font-semibold text-foreground uppercase tracking-wider pl-1">
              STOCKS TRANSACTED
            </TableHead>
            <TableHead className="text-xs font-semibold text-foreground uppercase tracking-wider pr-4 pl-1">
              PRICE
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.keys(insider_grouped)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map((date) => {
              const items = insider_grouped[date];

              if (!items) return null;

              return items.map((item) => {
                return (
                  <TableRow
                    key={item.id}
                    className={`${item.acquisitionOrDisposition === "A" ? "bg-green-500/15 border-l-2 border-l-green-500/70" : item.acquisitionOrDisposition === "D" ? "bg-red-500/15 border-l-2 border-l-red-500/70" : ""}`}
                  >
                    <TableCell className="pl-5">
                      <div className="">
                        <span>
                          {format(item.transactionDate, "MMM dd, yyyy")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        prefetch={false}
                        href={item.url}
                        className="inline-flex items-center gap-2"
                        target="_blank"
                      >
                        {item.acquisitionOrDisposition === "A"
                          ? "Buy"
                          : item.acquisitionOrDisposition === "D"
                            ? "Sell"
                            : item.acquisitionOrDisposition === "I"
                              ? "Initial"
                              : "--"}
                        <SquareArrowOutUpRight size={12} />
                      </Link>
                    </TableCell>
                    <TableCell className="flex items-center gap-2">
                      {item.reportingName}
                      <Tooltip content="Ask Prism">
                        <Link
                          prefetch={false}
                          href={routes.shellPrompt(
                            `Who is ${item.reportingName}`,
                          )}
                        >
                          <CircleQuestionMarkIcon size={16} />
                        </Link>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{item.securitiesTransacted}</TableCell>
                    <TableCell>${item.price}</TableCell>
                  </TableRow>
                );
              });
            })}
        </TableBody>
      </Table>
    </div>
  );
}
